import { useState, useEffect, useCallback } from "react";
import { validateInvite, acceptInvite } from "../inviteApi";
import type {
  ValidateInviteState,
  AcceptInviteState,
  PasswordForm,
} from "../inviteTypes";
import {
  validatePassword,
  validatePasswordMatch,
} from "../utils/passwordValidation";
import { validate as isUuid } from "uuid";
import { trackException, trackInfo, LogLevel } from "../../../shared/services/appInsights";
import { AppInsightsActions, AppInsightsComponents, AppInsightsErrorTypes } from "../../../shared/services/appInsightsConstants";

export const useInvitation = (guid: string, token: string) => {
  const [validationState, setValidationState] = useState<ValidateInviteState>({
    status: "idle",
  });

  const [acceptState, setAcceptState] = useState<AcceptInviteState>({
    status: "idle",
  });

  const validateInvitation = useCallback(async () => {
    if (!guid || !token) {
      return;
    }

    setValidationState({ status: "validating" });
    
    // Track validation attempt
    trackInfo("Invite validation started", {
      component: AppInsightsComponents.USE_INVITATION_HOOK,
      action: AppInsightsActions.INVITE_VALIDATION_ATTEMPT,
      hasGuid: !!guid,
      hasToken: !!token,
      isValidUuid: isUuid(guid)
    });

    try {
      const response = await validateInvite(guid, token);
      if (response.data.response.isValid) {
        // Track successful validation
        trackInfo("Invite validation successful", {
          component: AppInsightsComponents.USE_INVITATION_HOOK,
          action: AppInsightsActions.INVITE_VALIDATION_SUCCESS,
          email: response.data.response.emailMasked || "unknown"
        });

        setValidationState({
          status: "valid",
          email: response.data.response.emailMasked,
        });
      } else {
        // Track specific validation failure reasons
        const reason = response.data.response.reason;
        trackInfo("Invite validation failed", {
          component: AppInsightsComponents.USE_INVITATION_HOOK,
          action: AppInsightsActions.INVITE_VALIDATION_FAILURE,
          reason: reason,
          errorType: reason === "expired" ? AppInsightsErrorTypes.INVITE_EXPIRED_ERROR :
                    reason === "used" ? AppInsightsErrorTypes.INVITE_USED_ERROR :
                    reason === "revoked" ? AppInsightsErrorTypes.INVITE_REVOKED_ERROR :
                    AppInsightsErrorTypes.INVITE_VALIDATION_ERROR
        });

        if (reason === "invalid") {
          setValidationState({
            status: "invalid",
            errorMessage: "This invitation link is invalid or has expired.",
          });
        } else if (reason === "expired") {
          setValidationState({
            status: "invalid",
            errorMessage: "This invitation link has expired.",
          });
        } else if (reason === "used") {
          setValidationState({
            status: "invalid",
            errorMessage: "This invitation link has already been used.",
          });
        } else if (reason === "revoked") {
          setValidationState({
            status: "invalid",
            errorMessage: "This invitation link has been revoked.",
          });
        } else {
          setValidationState({
            status: "error",
            errorMessage: "This invitation link is not found.",
          });
        }
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      
      // Track validation API error
      trackException(
        error as Error,
        LogLevel.Error,
        {
          component: AppInsightsComponents.USE_INVITATION_HOOK,
          action: AppInsightsActions.INVITE_VALIDATION_FAILURE,
          errorType: AppInsightsErrorTypes.API_ERROR,
          errorMessage: err.response?.data?.message || "Failed to validate invitation link."
        }
      );

      setValidationState({
        status: "error",
        errorMessage:
          err.response?.data?.message || "Failed to validate invitation link.",
      });
    }
  }, [guid, token]);

  // Validate invitation on mount
  useEffect(() => {
    if (isUuid(guid) && token) {
      // Avoid calling setState synchronously in effect
      (async () => {
        await validateInvitation();
      })();
    } else {
      setTimeout(() => {
        setValidationState({
          status: "invalid",
          errorMessage: "Invalid invitation link.",
        });
      }, 0);
    }
  }, [guid, token, validateInvitation]);

  const acceptInvitation = async (passwordForm: PasswordForm) => {
    if (!guid || !token) return;
    // Validate password match
    const matchValidation = validatePasswordMatch(
      passwordForm.password,
      passwordForm.confirmPassword
    );
    if (!matchValidation.isValid) {
      setAcceptState({
        status: "error",
        errorMessage: matchValidation.error || "Passwords do not match.",
      });
      return;
    }

    // Comprehensive password validation
    const passwordValidation = validatePassword(passwordForm.password);
    if (!passwordValidation.isValid) {
      // Track password validation failure
      trackInfo("Invite password validation failed", {
        component: AppInsightsComponents.USE_INVITATION_HOOK,
        action: AppInsightsActions.INVITE_PASSWORD_VALIDATION_FAILURE,
        errorType: AppInsightsErrorTypes.VALIDATION_ERROR,
        validationErrors: passwordValidation.errors.join(", ")
      });

      setAcceptState({
        status: "error",
        errorMessage: passwordValidation.errors.join(" "),
      });
      return;
    }

    setAcceptState({ status: "submitting" });

    try {
      const response  = await acceptInvite({
        inviteId: guid,
        password: passwordForm.password,
        token: token,
        // Add other required fields based on your API
      });

      if (response.data.response.isCompleted) {
        // Track successful invite acceptance
        trackInfo("Invite accepted successfully", {
          component: AppInsightsComponents.USE_INVITATION_HOOK,
          action: AppInsightsActions.INVITE_ACCEPT_SUCCESS
        });

        setAcceptState({ status: "success" });
      } else {
        const reason = response.data.response.reason;
        
        // Track acceptance failure with specific reason
        trackInfo("Invite acceptance failed", {
          component: AppInsightsComponents.USE_INVITATION_HOOK,
          action: AppInsightsActions.INVITE_ACCEPT_FAILURE,
          reason: reason,
          errorType: AppInsightsErrorTypes.API_ERROR
        });

        if (reason === "not_found") {
          setAcceptState({
            status: "error",
            errorMessage: "User account is not found.",
          });
        } else if (reason === "user_account_issue") {
          setAcceptState({
            status: "error",
            errorMessage: "User account is disabled or locked.",
          });
        } else {
          setAcceptState({
            status: "error",
            errorMessage: "Failed to set up account.",
          });
        }
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      
      // Track acceptance API error
      trackException(
        error as Error,
        LogLevel.Error,
        {
          component: AppInsightsComponents.USE_INVITATION_HOOK,
          action: AppInsightsActions.INVITE_ACCEPT_FAILURE,
          errorType: AppInsightsErrorTypes.API_ERROR,
          errorMessage: err.response?.data?.message || "Failed to set up account."
        }
      );

      setAcceptState({
        status: "error",
        errorMessage:
          err.response?.data?.message || "Failed to set up account.",
      });
    }
  };

  return {
    validationState,
    acceptState,
    validateInvitation,
    acceptInvitation,
    resetAcceptState: () => setAcceptState({ status: "idle" }),
  };
};
